import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo } from "react";
import { RecommendedLocations } from "./recommendedLocations";
import { TYPE_TAG } from "./interface/typeTag";
import { container } from "tsyringe";
/**
 * Renders a quick search component for tags, displaying different UI elements
 * based on the provided `tag` prop. Supports rendering recommended locations
 * and driver license information.
 *
 * @param props - The properties for the TagsQuickSearch component.
 * @param props.tag - The type of tag to render, determining which UI to display.
 * @param props.recommendedLocations - (Optional) An array of recommended locations to display when the tag is for recommended locations.
 * @param props.setSelectedPickupLocation - (Optional) Callback to set the selected pickup location.
 * @param props.setSelectedReturnLocation - (Optional) Callback to set the selected return location.
 * @param props.setLicenseVisible - (Optional) Callback to set the visibility of the license information.
 *
 * @returns The corresponding tag UI component or null if the tag type is not recognized.
 */
export const TagsQuickSearch = (props) => {
    // Resolver DI
    const dataStore = container.resolve("IUseStoreAplication");
    const { activeTag, recommendedLocations = [] } = dataStore.app;
    const { countryCode } = dataStore.ipLocation;
    const { setActiveTag, setRecommendedLocations } = dataStore.actions;
    const { setSelectedPickupLocation, setSelectedReturnLocation, setLicenseVisible, bypass } = props;
    // Clear recommendations immediately when the URL changes so stale
    // tags from the previous page are never visible during navigation.
    useEffect(() => {
        const initialUrl = window.location.href;
        const clearIfNavigated = () => {
            if (window.location.href !== initialUrl) {
                setRecommendedLocations([]);
                setActiveTag(TYPE_TAG.NONE);
            }
        };
        // Detect browser back/forward
        window.addEventListener("popstate", clearIfNavigated);
        // Detect programmatic navigation (pushState/replaceState)
        const origPushState = window.history.pushState.bind(window.history);
        const origReplaceState = window.history.replaceState.bind(window.history);
        window.history.pushState = (...args) => { origPushState(...args); clearIfNavigated(); };
        window.history.replaceState = (...args) => { origReplaceState(...args); clearIfNavigated(); };
        return () => {
            window.removeEventListener("popstate", clearIfNavigated);
            window.history.pushState = origPushState;
            window.history.replaceState = origReplaceState;
        };
    }, []);
    /**
     * tagRender is a memoized value that determines which tag UI component to render
     */
    const tagRender = useMemo(() => {
        const tags = {
            [TYPE_TAG.RECOMMENDED_LOCATIONS]: (_jsx(RecommendedLocations, { setSelectedPickupLocation: setSelectedPickupLocation, setSelectedReturnLocation: setSelectedReturnLocation })),
            [TYPE_TAG.DRIVER_LICENSE_INFO]: //<DriverLicenseInfo />,
            _jsx(_Fragment, {})
        };
        return tags[String(activeTag)] || null;
    }, [activeTag, setSelectedPickupLocation, setSelectedReturnLocation, setLicenseVisible]);
    /**
     * Effect hook to determine the active tag based on recommended locations,
     * session storage values, and the user's country.
     *
     * Priority:
     * 1. If recommendedLocations exist -> show RECOMMENDED_LOCATIONS (any country, including US)
     * 2. If US or bypass and no recs -> show DRIVER_LICENSE_INFO
     * 3. Otherwise -> NONE
     */
    useEffect(() => {
        const country = countryCode !== null && countryCode !== void 0 ? countryCode : "";
        const isUS = country.toLowerCase() === "us";
        // Always show license for US users
        if (isUS || bypass) {
            setLicenseVisible(true);
        }
        // Priority 1: recommended locations only on /pages/ landing routes (AB#8119)
        const isLandingPage = window.location.pathname.includes("/pages/");
        if (recommendedLocations.length > 0 && isLandingPage) {
            setActiveTag(TYPE_TAG.RECOMMENDED_LOCATIONS);
        }
        else if (isUS || bypass) {
            // Priority 2: US/bypass without recs -> driver license info
            setActiveTag(TYPE_TAG.DRIVER_LICENSE_INFO);
        }
        else {
            setActiveTag(TYPE_TAG.NONE);
        }
    }, [countryCode, bypass, recommendedLocations]);
    return tagRender;
};
